import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, Leaf, Heart, Sparkles, Users, ChevronDown, Briefcase } from 'lucide-react';
import BreathingRoomAttribution from '../components/BreathingRoomAttribution';

const BOOKING_URL = "https://www.wellnessliving.com/rs/appointment-new/breathing_room-rws2se?dtu_redirect=2026-01-23+21%3A42%3A29&id_class_tab=3&id_mode=1&s_id=IXbkTr";

const ServicesPage = () => {
  const [expandedItems, setExpandedItems] = useState({});

  const toggleItem = (serviceId, itemIndex) => {
    const key = `${serviceId}-${itemIndex}`;
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const services = [
    {
      id: 'movement',
      icon: Leaf,
      category: 'Restorative Movement',
      title: 'Holistic/Restorative Personal Training',
      subtitle: 'Restoring function and well movement',
      description: 'Meeting your body where it is. Developing functional movement and a healthy connection through various modalities. When we\'ve had setbacks, pain, or disconnection, we must find new and innovative ways to meet the body and return to the wisdom we once knew.',
      includes: [
        {
          title: 'Human Garage Fascial Maneuvers',
          content: {
            heading: 'Fascial Maneuvers at Breathing Room',
            subheading: 'with Jill | Grounded Wellness',
            intro: "If your body has been whispering…or shouting…\ntightness, stress, fatigue, pain, or simply a sense that something feels \"off\"…\nthis space is for you.\n\nJoin us at Breathing Room Bermuda for guided Fascial Maneuvers sessions designed to help you release tension, reconnect with your body, and move with greater ease.\n\nThis is not about pushing harder.\nIt's about listening deeper.",
            sections: [
              {
                title: 'What Are Fascial Maneuvers?',
                text: 'Fascial Maneuvers, developed by Human Garage in 2020, are gentle, intentional movement sequences designed to work with the body\'s fascial system.\n\nRather than focusing on muscles alone, we explore:',
                bullets: [
                  'slow, mindful movement',
                  'rotation and counter-rotation',
                  'breath-led release',
                  'supportive body positioning',
                ],
                afterBullets: 'These movements help unwind tension patterns held in the body—physically, emotionally, and neurologically.',
                benefits: [
                  'reduce stress and physical tension quickly, allowing the body to naturally reorganize and realign',
                  'improve mobility, circulation, and overall fluid movement',
                  'support the release of stored emotional patterns while creating a quieter, more settled mind',
                  'regulate the nervous system, helping the body shift out of stress and into repair',
                  'support hormonal balance by lowering stress load (including cortisol) and improving overall system communication',
                  'create space for the body to heal and restore over time',
                  'and some sort of magic that\'s difficult to describe unless you practice and experience it',
                ],
                benefitsIntro: 'In practice, they can:',
              },
              {
                title: 'What Is Fascia?',
                text: "Fascia is the body's connective web—\na continuous, intelligent network that wraps around and through everything:",
                inlineList: ['muscles', 'bones', 'organs', 'nerves', 'blood vessels'],
                afterList: "…but more than structure, it's communication.\n\nFascia responds to stress, posture, injury, and even emotional experience.\nIt holds patterns. It adapts. It remembers.\n\nWhen fascia becomes restricted, we often feel it as:\ntightness, discomfort, reduced mobility, or fatigue.\n\nWhen it begins to release…\nthe body often feels lighter, freer, and more fluid.",
              },
              {
                title: 'What to Wear',
                text: 'Come as you are—just comfortably.\n\nThink:',
                bullets: [
                  'leggings or stretch pants',
                  'loose, breathable clothing',
                  'anything you can move and relax in',
                ],
                afterBullets: 'You may occasionally need access to your knees, so clothing that allows for that is helpful.\n\nNo special gear needed.\nChairs are available if you prefer a seated practice.',
              },
            ],
          },
        },
        {
          title: 'Hands-on Fascial Body Work',
          content: {
            heading: 'Body / Emotion Alignment',
            subheading: 'Hands-on Fascial Body Work with Jill',
            intro: "A body/emotion alignment session is designed to release stuck and stored emotions and stored stress from the entire body — muscle and fascia tissues, the nervous system, organs, and meridian lines.",
            sections: [
              {
                title: 'What to Expect',
                text: "This is a hands-on session that resembles acupressure, and muscle/fascia and organ release.\n\nIt is thorough and fairly swift as we move energy out through the entire body — quickly, before it can settle back in — before sweeping it out.\n\nThis is not a relaxation session.\nIt is an active session for both of us.",
              },
              {
                title: 'Pricing',
                text: 'The initial session includes extra time for intake and history gathering.',
                bullets: [
                  'Initial session — $125 for roughly 60–75 minutes (history is obtained)',
                  'Follow-up sessions — $125 for 60 minutes',
                  'Extended time — option to carry on at a rate of $100/hour',
                ],
              },
              {
                title: 'Sensitivities & Who It\'s For',
                text: "Because of the nature of the work, those in chronic pain or in a delicate state must declare sensitivities prior to the session commencing.\n\nThere are gentler methods to call upon in all situations.\n\nThis is a conversation — between you, your body, your emotions, and myself as practitioner.",
              },
            ],
            bookingLink: {
              label: 'Ready to book? Reserve your session',
              url: 'https://www.wellnessliving.com/rs/appointment-new/breathing_room-rws2se?dtu_redirect=2026-05-03+23%3A28%3A31&id_class_tab=3&id_mode=1&s_id=GEsFIv',
            },
          },
        },
        { title: 'Functional movement assessment', content: null },
        { title: 'Personalized movement protocols', content: null },
        { title: 'Breath work integration', content: null },
        { title: 'Pain pattern recognition', content: null },
        { title: 'Body awareness training', content: null },
      ],
      formats: [
        {
          label: 'Private Sessions',
          items: [
            { name: '60 minutes', price: '$110' },
            { name: '45 minutes', price: '$90' },
            { name: '30 minutes', price: '$70' },
            { name: 'Duo Session (1 hr)', price: '$220' },
            { name: 'Fascial Maneuvers — Hands-on', price: '$125' },
          ],
          note: 'Solo session rate applies across all services & coaching. Only Movement (PT) sessions are available in 45- or 30-minute formats.',
        },
        {
          label: 'Packages',
          items: [
            { name: '60 min sessions', heading: true },
            { name: '5 sessions', price: '$522.50 ($104.50/ea)' },
            { name: '10 sessions', price: '$990 ($99/ea)' },
            { name: '20 sessions', price: '$1,870 ($93.50/ea)' },
            { name: '45 min sessions', heading: true },
            { name: '5 sessions', price: '$427.50 ($85.50/ea)' },
            { name: '10 sessions', price: '$810 ($81/ea)' },
            { name: '20 sessions', price: '$1,530 ($76.50/ea)' },
            { name: '30 min sessions', heading: true },
            { name: '5 sessions', price: '$332.50 ($66.50/ea)' },
            { name: '10 sessions', price: '$630 ($63/ea)' },
            { name: '20 sessions', price: '$1,190 ($59.50/ea)' },
          ],
          note: 'Solo (60 min) rate applies across all services & coaching. 45- & 30-minute packages are Movement (PT) only.',
        },
        {
          label: 'Group Movement Classes',
          items: [
            { name: 'In-studio regular classes', price: '$25' },
            { name: 'Beach Fascial Maneuvers / Specialized classes', price: '$35' },
          ],
          note: 'Workshops available — reach out for upcoming dates and pricing.',
        },
      ],
      color: 'bg-emerald-50 border-emerald-200',
      iconBg: 'bg-emerald-100',
      image: 'https://customer-assets.emergentagent.com/job_hello-app-881/artifacts/wvzurfaz_AHHW%20Kbell3.png',
    },
    {
      id: 'health',
      icon: Heart,
      category: 'Holistic Health Coaching',
      title: 'Holistic Wellness and Nutrition Coaching',
      subtitle: 'Foods, sleep, stress, digestion, detox',
      description: 'We are an integrated system—each system impacts the others. Our stress is not separate from our body. I use metabolic typing and gut healing protocols through eating styles and anti-inflammatory approaches, rooted in the philosophy of Breaking the Vicious Cycle and SCD.',
      includes: [
        { title: 'Metabolic typing assessment', content: null },
        { title: 'Gut healing protocols', content: null },
        { title: 'Anti-inflammatory diet planning', content: null },
        { title: 'Sleep optimization strategies', content: null },
        { title: 'Stress management techniques', content: null },
        { title: 'Detox support', content: null },
        { title: 'Digestive health focus', content: null },
      ],
      formats: [
        {
          label: 'Private Coaching',
          items: [
            { name: 'Per hour', price: '$110' },
          ],
          note: 'Packages available — reach out for pricing.',
        },
        {
          label: 'Health Immersion Programs',
          items: [
            { name: 'C.R.A.P. Cleanse', price: '' },
            { name: '21-Day Holistic Detox', price: '' },
            { name: 'Slow Wellness', price: '' },
          ],
          note: 'Visit the Programs page for details, start dates, and enrollment.',
        },
      ],
      color: 'bg-rose-50 border-rose-200',
      iconBg: 'bg-rose-100',
      image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=800',
    },
    {
      id: 'coaching',
      icon: Sparkles,
      category: 'Holistic Coaching (IMESP)',
      title: 'Whole Self Coaching',
      subtitle: 'Through the 8 pillars of wellness',
      description: 'Meeting you where you are in this challenge and phase of life. Deeper self-work through the Intuitive, Mental, Emotional, Spiritual, and Physical realms. We are physical beings having a spiritual existence. When we have a physical setback, we often need to introduce a whole method of healing.',
      includes: [
        { title: 'IMESP framework exploration', content: null },
        { title: 'Belief systems work', content: null },
        { title: 'Emotional release techniques', content: null },
        { title: 'Energy stagnation clearing', content: null },
        { title: 'Trauma-informed approaches', content: null },
        { title: 'Identity integration', content: null },
        { title: 'Spiritual grounding practices', content: null },
      ],
      formats: [
        {
          label: 'Private Deep-Dive Sessions',
          items: [
            { name: 'Per hour', price: '$110' },
          ],
          note: 'Packages available — reach out for pricing.',
        },
        {
          label: 'Immersion Programs',
          items: [
            { name: 'Half-day to two-day programs available', price: '' },
          ],
          note: 'Reach out for upcoming dates and custom immersion planning.',
        },
      ],
      color: 'bg-violet-50 border-violet-200',
      iconBg: 'bg-violet-100',
      image: 'https://customer-assets.emergentagent.com/job_hello-app-881/artifacts/xt5ol5r0_1668108262556.jpg',
    },
    {
      id: 'corporate',
      icon: Briefcase,
      category: 'Corporate Wellness',
      title: 'Corporate Wellness',
      subtitle: 'Wellness that comes to you',
      description: "Is it time to bring Wellness within your Company? Support your team with grounded, approachable wellness experiences — delivered on-site or virtually. Three flexible offerings designed to reduce stress, improve focus, and invite your people back to themselves.",
      includes: [
        {
          title: 'M M M — Meditation, Mindfulness, Movement',
          content: {
            heading: 'M M M',
            subheading: 'Meditation · Mindfulness · Movement',
            intro: "Group classes that break up the day, reset the nervous system, and bring your team back into their bodies.",
            sections: [
              {
                title: 'What It Looks Like',
                text: "We tailor each session to the mood and needs of the day — and the peeps in the room. Ditch stress, find breath and movement, and freshen up the workday.",
                bullets: [
                  'On-site or virtual group classes',
                  'Flexible length — 20, 30, or 45 minute formats',
                  'Adapted for any fitness / mobility level',
                  'Works in boardrooms, studios, or outdoor spaces',
                ],
              },
              {
                title: 'Various Sessions Offered',
                text: 'A flexible mix of formats — chosen with you to match your team\'s energy, space, and goals.',
                items: [
                  { term: 'Meditation & Mind-Calming Practices', definition: 'simple, accessible techniques to quiet the mind and improve focus' },
                  { term: 'Parasympathetic Tools', definition: 'practical "stress busters" and "tension tamers" that help regulate the nervous system in real time' },
                  { term: 'Fascial Maneuvers / Flow', definition: 'gentle yet powerful movements that release stored tension and stress from the body (often felt immediately)' },
                  { term: 'Restorative Movement & Stretching', definition: 'to support posture, reduce physical strain, and improve overall wellbeing' },
                  { term: 'Yoga & Guided Movement Classes', definition: 'adaptable to all levels' },
                  { term: "Let's Dance (Free Movement)", definition: 'a light, energizing way to move, reset, and reconnect' },
                ],
              },
            ],
          },
        },
        {
          title: 'Chew On This — Lunch & Learn',
          content: {
            heading: 'Chew On This',
            subheading: 'Lunch & Learn Sessions',
            intro: "Lunch and Learn sessions on a variety of health, wellness, and mindfulness topics. Bring your lunch, a curious mind, and an open heart.",
            sections: [
              {
                title: 'How It Works',
                text: "A wide range of topics is available, and I run a light needs assessment beforehand to make sure the session lands with your company and participants.",
                bullets: [
                  'Sample topics — stress & the nervous system, sleep, gut health, fascia & posture, metabolic typing, burnout, boundaries',
                  'Typically 45–60 minutes including Q&A',
                  'Topic chosen collaboratively with your team',
                  'Can be a one-off or an ongoing series',
                ],
              },
            ],
          },
        },
        {
          title: 'Wellness Collective — 7-Session Group Program',
          content: {
            heading: 'Wellness Collective',
            subheading: 'A comprehensive group wellness program',
            intro: "A 7-session group program designed to assess and address multiple areas of wellness — movement, breath, stress, sleep, nutrition, mindset, and integration.",
            sections: [
              {
                title: 'How It Flexes',
                text: "The program can be delivered anywhere between 7 weeks and 6 months. Spread the sessions out for more implementation practice, or condense them for heightened motivation and concise learning.",
                bullets: [
                  '7 sessions · your timeline',
                  'Initial wellness assessment + follow-through',
                  'Blend of movement, education, and guided practice',
                  'Can be paired with MMM and Chew On This for a full-spectrum program',
                ],
              },
            ],
          },
        },
      ],
      formats: [
        {
          label: 'Custom Packages',
          items: [
            { name: 'Tailored to company size, goals, and budget', price: '' },
          ],
          note: 'Reach out to book a discovery conversation — I design each corporate engagement around the specific needs and rhythm of your team.',
        },
      ],
      color: 'bg-sky-50 border-sky-200',
      iconBg: 'bg-sky-100',
      image: 'https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=800',
    },
  ];

  const sessionTypes = [
    {
      icon: Users,
      title: 'Private Sessions',
      description: 'One-on-one focused work tailored specifically to your needs, goals, and pace.',
      ideal: 'Best for deep personal transformation and specific health challenges.',
    },
    {
      icon: Users,
      title: 'Group Wellness Sessions',
      description: 'Movement-oriented group classes with a focus on functional restoration and body connection.',
      ideal: 'Best for ongoing movement practice and community support.',
    },
    {
      icon: Users,
      title: 'Larger Group Programs',
      description: 'Educational workshops, deeper wellness immersion programs, and IMESP healing experiences.',
      ideal: 'Best for comprehensive learning and transformation in community.',
    },
  ];

  return (
    <div className="bg-cream pt-24" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">
              Services
            </p>
            <h1 className="font-heading text-5xl md:text-6xl text-forest leading-[1.1] mb-6">
              Four Pathways to<br />
              <span className="italic text-terracotta">Whole Self Wellness</span>
            </h1>
            <p className="text-stone-600 text-lg font-body leading-relaxed">
              Every service I offer works toward the same goal: helping you reconnect with 
              your body, restore your health, and integrate all aspects of yourself. 
              Choose the entry point that calls to you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-16 md:space-y-24">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              data-testid={`service-${service.id}`}
              className={`rounded-3xl border p-8 md:p-12 ${service.color}`}
            >
              <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
                <div>
                  <div className={`w-16 h-16 ${service.iconBg} rounded-2xl flex items-center justify-center mb-6`}>
                    <service.icon size={28} className="text-forest" />
                  </div>
                  <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-2">
                    {service.category}
                  </p>
                  <h2 className="font-heading text-3xl md:text-4xl text-forest mb-3">
                    {service.title}
                  </h2>
                  <p className="text-stone-500 font-body text-lg mb-6 italic">
                    {service.subtitle}
                  </p>
                  <p className="text-stone-600 font-body text-lg leading-relaxed mb-8">
                    {service.description}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {service.formats.map((format, fi) => {
                      const isObj = typeof format === 'object';
                      const label = isObj ? format.label : format;
                      const key = `${service.id}-fmt-${fi}`;
                      const isOpen = !!expandedItems[key];
                      const hasContent = isObj && (format.items?.length > 0 || format.note);
                      return (
                        <div key={label} className="relative">
                          <button
                            type="button"
                            onClick={() => hasContent && setExpandedItems(p => ({ ...p, [key]: !p[key] }))}
                            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-body text-sm transition-colors ${
                              hasContent
                                ? (isOpen ? 'bg-forest text-cream' : 'bg-forest/10 text-forest hover:bg-forest/20 cursor-pointer')
                                : 'bg-forest/10 text-forest cursor-default'
                            }`}
                            data-testid={`format-pill-${service.id}-${fi}`}
                          >
                            {label}
                            {hasContent && (
                              <ChevronDown
                                size={14}
                                className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
                              />
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Expanded format details */}
                  <AnimatePresence>
                    {service.formats.map((format, fi) => {
                      if (typeof format !== 'object') return null;
                      const key = `${service.id}-fmt-${fi}`;
                      if (!expandedItems[key]) return null;
                      return (
                        <motion.div
                          key={key}
                          initial={{ opacity: 0, height: 0, marginTop: 0 }}
                          animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
                          exit={{ opacity: 0, height: 0, marginTop: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                          data-testid={`format-panel-${service.id}-${fi}`}
                        >
                          <div className="bg-white/70 border border-forest/20 rounded-xl p-5">
                            <p className="text-xs uppercase tracking-[0.2em] text-[#8a6a5a] font-body mb-3">
                              {format.label}{format.items?.some(it => it.price) ? ' · Rates' : ''}
                            </p>
                            <ul className="divide-y divide-stone-200">
                              {format.items?.map((it, ii) => (
                                it.heading ? (
                                  <li key={ii} className="pt-3 pb-1">
                                    <span className="font-body text-xs uppercase tracking-[0.15em] text-terracotta font-semibold">{it.name}</span>
                                  </li>
                                ) : (
                                  <li key={ii} className="flex items-center justify-between py-2">
                                    <span className="font-body text-stone-700 text-sm">{it.name}</span>
                                    <span className="font-body text-forest font-medium text-sm">{it.price}</span>
                                  </li>
                                )
                              ))}
                            </ul>
                            {format.note && (
                              <p className="text-xs font-body italic text-stone-500 mt-3 leading-relaxed">
                                {format.note}
                              </p>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>

                <div className="space-y-6">
                  {service.image && (
                    <div className="rounded-2xl overflow-hidden">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className={`w-full ${service.id === 'coaching' ? 'h-auto' : 'h-48 object-cover object-center'}`}
                      />
                    </div>
                  )}
                  <div className="bg-white/60 rounded-2xl p-6 md:p-8">
                    <h3 className="font-heading text-xl text-forest mb-6">What's Included</h3>
                    <ul className="space-y-2">
                      {service.includes.map((item, i) => {
                        const key = `${service.id}-${i}`;
                        const isExpanded = expandedItems[key];
                        const hasContent = item.content !== null;
                        return (
                          <li key={i} className="overflow-hidden">
                            <button
                              onClick={() => hasContent && toggleItem(service.id, i)}
                              className={`flex items-start gap-3 w-full text-left p-3 rounded-xl transition-all ${
                                hasContent
                                  ? 'hover:bg-forest/5 cursor-pointer group'
                                  : 'cursor-default'
                              } ${isExpanded ? 'bg-forest/5' : ''}`}
                              data-testid={`service-item-${service.id}-${i}`}
                            >
                              <Check size={20} className={`flex-shrink-0 mt-0.5 transition-colors ${
                                isExpanded ? 'text-forest' : 'text-terracotta'
                              }`} />
                              <span className={`font-body flex-1 ${
                                isExpanded ? 'text-forest font-medium' : 'text-stone-600'
                              }`}>{item.title}</span>
                              {hasContent && (
                                <ChevronDown size={18} className={`text-stone-400 flex-shrink-0 mt-0.5 transition-transform ${
                                  isExpanded ? 'rotate-180 text-forest' : 'group-hover:text-forest'
                                }`} />
                              )}
                            </button>

                            <AnimatePresence>
                              {isExpanded && item.content && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="overflow-hidden"
                                >
                                  <div className="ml-4 mr-2 mb-3 mt-1 bg-white rounded-xl border border-stone-100 p-5 sm:p-6 space-y-5">
                                    {/* Heading & subheading */}
                                    {item.content.heading && (
                                      <div className="border-b border-stone-100 pb-4">
                                        <h4 className="font-heading text-xl text-forest">{item.content.heading}</h4>
                                        {item.content.subheading && (
                                          <p className="font-accent text-sm text-terracotta italic mt-1">{item.content.subheading}</p>
                                        )}
                                      </div>
                                    )}

                                    {/* Intro text */}
                                    {item.content.intro && (
                                      <div>
                                        {item.content.intro.split('\n\n').map((para, pi) => (
                                          <p key={pi} className={`font-body text-sm leading-relaxed mb-3 ${
                                            para.includes('not about pushing') || para.includes('listening deeper')
                                              ? 'text-forest font-medium italic'
                                              : 'text-stone-600'
                                          }`}>
                                            {para.split('\n').map((line, li) => (
                                              <React.Fragment key={li}>
                                                {line}
                                                {li < para.split('\n').length - 1 && <br />}
                                              </React.Fragment>
                                            ))}
                                          </p>
                                        ))}
                                      </div>
                                    )}

                                    {/* Content sections */}
                                    {item.content.sections?.map((section, si) => (
                                      <div key={si} className="pt-2">
                                        <div className="flex items-center gap-2 mb-3">
                                          <div className="w-2 h-2 rounded-full bg-terracotta" />
                                          <h5 className="font-heading text-lg text-forest">{section.title}</h5>
                                        </div>

                                        {section.text && (
                                          <div className="mb-3">
                                            {section.text.split('\n\n').map((p, pi) => (
                                              <p key={pi} className="font-body text-sm text-stone-600 leading-relaxed mb-2">
                                                {p.split('\n').map((line, li) => (
                                                  <React.Fragment key={li}>
                                                    {line}
                                                    {li < p.split('\n').length - 1 && <br />}
                                                  </React.Fragment>
                                                ))}
                                              </p>
                                            ))}
                                          </div>
                                        )}

                                        {/* Bullets */}
                                        {section.bullets && (
                                          <ul className="space-y-1.5 mb-3 ml-1">
                                            {section.bullets.map((b, bi) => (
                                              <li key={bi} className="flex items-start gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-1.5 flex-shrink-0" />
                                                <span className="font-body text-sm text-stone-600">{b}</span>
                                              </li>
                                            ))}
                                          </ul>
                                        )}

                                        {/* Items (term + definition pairs) */}
                                        {section.items && (
                                          <ul className="space-y-2 mb-3 ml-1">
                                            {section.items.map((it, ii) => (
                                              <li key={ii} className="flex items-start gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-2 flex-shrink-0" />
                                                <span className="font-body text-sm text-stone-600">
                                                  <span className="font-semibold text-forest">{it.term}</span>
                                                  {' — '}
                                                  {it.definition}
                                                </span>
                                              </li>
                                            ))}
                                          </ul>
                                        )}

                                        {section.afterBullets && (
                                          <p className="font-body text-sm text-stone-600 leading-relaxed mb-3">{section.afterBullets}</p>
                                        )}

                                        {/* Benefits with intro */}
                                        {section.benefitsIntro && (
                                          <div className="bg-forest/5 rounded-lg p-4 mb-3">
                                            <p className="font-body text-sm text-forest font-medium mb-2">{section.benefitsIntro}</p>
                                            <ul className="space-y-1.5">
                                              {section.benefits.map((b, bi) => (
                                                <li key={bi} className="flex items-start gap-2">
                                                  <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-1.5 flex-shrink-0" />
                                                  <span className="font-body text-sm text-stone-600">{b}</span>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}

                                        {/* Inline list (for fascia body parts) */}
                                        {section.inlineList && (
                                          <div className="flex flex-wrap gap-2 mb-3">
                                            {section.inlineList.map((item2, ii) => (
                                              <span key={ii} className="bg-stone-100 text-stone-600 px-3 py-1 rounded-full font-body text-xs">
                                                {item2}
                                              </span>
                                            ))}
                                          </div>
                                        )}

                                        {section.afterList && (
                                          <div>
                                            {section.afterList.split('\n\n').map((p, pi) => (
                                              <p key={pi} className="font-body text-sm text-stone-600 leading-relaxed mb-2">
                                                {p.split('\n').map((line, li) => (
                                                  <React.Fragment key={li}>
                                                    {line}
                                                    {li < p.split('\n').length - 1 && <br />}
                                                  </React.Fragment>
                                                ))}
                                              </p>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ))}

                                    {/* Simple text content */}
                                    {item.content.text && !item.content.sections && (
                                      <p className="font-body text-sm text-stone-600 leading-relaxed">{item.content.text}</p>
                                    )}

                                    {/* Booking link (soft text-link) */}
                                    {item.content.bookingLink && (
                                      <div className="pt-2">
                                        <a
                                          href={item.content.bookingLink.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          data-testid={`booking-link-${service.id}-${i}`}
                                          className="inline-flex items-center gap-2 font-body text-sm text-terracotta hover:text-forest border-b border-terracotta/40 hover:border-forest pb-0.5 transition-colors"
                                        >
                                          {item.content.bookingLink.label}
                                          <ArrowRight size={14} />
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Session Types */}
      <section className="py-16 md:py-24 bg-stone-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">
              How We Work Together
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-forest">
              Session Formats
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {sessionTypes.map((type, index) => (
              <motion.div
                key={type.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                data-testid={`session-type-${index + 1}`}
                className="bg-white p-8 rounded-2xl shadow-card"
              >
                <div className="w-14 h-14 bg-terracotta/10 rounded-full flex items-center justify-center mb-6">
                  <type.icon size={24} className="text-terracotta" />
                </div>
                <h3 className="font-heading text-xl text-forest mb-4">{type.title}</h3>
                <p className="text-stone-600 font-body leading-relaxed mb-4">
                  {type.description}
                </p>
                <p className="text-terracotta font-body text-sm">
                  {type.ideal}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-forest text-cream">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-4xl md:text-5xl mb-6">
              Not Sure Where to Start?
            </h2>
            <p className="text-cream/80 text-lg font-body leading-relaxed mb-10 max-w-2xl mx-auto">
              That's completely normal. Many clients come to me feeling overwhelmed by options. 
              Let's have a conversation and find the right starting point for you.
            </p>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-book-btn"
              className="bg-terracotta text-white px-8 py-4 rounded-full font-body font-medium hover:bg-terracotta-dark transition-all duration-300 inline-flex items-center gap-2"
            >
              Book a Discovery Session
              <ArrowRight size={18} />
            </a>
            <div className="mt-6 flex justify-center">
              <BreathingRoomAttribution align="center" theme="dark" />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
